/**
 * Страница входа в систему — красивая форма с градиентами и эффектами
 * Дата: 15.01.2026
 * Дата: 05.02.2026 - TODO-054: добавлен OTP login режим
 */
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { LoginRequest } from '@/api/types';
import { Loader2, User, Lock, Building2, LogIn, AlertCircle, Smartphone, KeyRound } from 'lucide-react';
import { authApi } from '@/api/auth';

export function LoginPage() {
  // Password login state
  const [form, setForm] = useState<LoginRequest>({
    username: '',
    password: '',
    tenant_slug: '',
  });
  
  // OTP login state (TODO-054, 05.02.2026)
  const [loginMode, setLoginMode] = useState<'password' | 'otp'>('password');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [channel, setChannel] = useState<'waba' | 'whatsapp' | 'sms' | ''>('');
  
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, loginWithOTP } = useAuth(); // TODO-054: добавлен loginWithOTP
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as any)?.from?.pathname || '/';

  // Password login handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(form);
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Ошибка входа. Проверьте данные.');
    } finally {
      setIsLoading(false);
    }
  };

  // OTP: Send code
  const handleSendCode = async (useFallback = false) => {
    setError('');
    setIsLoading(true);

    try {
      const result = await authApi.otpSend({ phone, use_fallback: useFallback });
      setCodeSent(true);
      setChannel(result.channel);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Не удалось отправить код');
    } finally {
      setIsLoading(false);
    }
  };

  // OTP: Verify code (TODO-054, 05.02.2026)
  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await loginWithOTP({ phone, code });
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Неверный код');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout>
      {/* Logo & Title */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm mb-4 ring-1 ring-white/20">
          <svg 
            className="w-8 h-8 text-white" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-white mb-1">MedIntegra Notifier</h1>
        <p className="text-white/60 text-sm">Система уведомлений для клиник</p>
      </div>

      {/* Login Card */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl p-8 ring-1 ring-white/20">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-neutral-800">Вход в систему</h2>
          <p className="text-neutral-500 text-sm mt-1">Введите ваши учётные данные</p>
        </div>

        {/* Mode Switcher (TODO-054, 05.02.2026) */}
        <div className="flex gap-2 mb-6 p-1 bg-neutral-100 rounded-xl">
          <button
            type="button"
            onClick={() => {
              setLoginMode('password');
              setError('');
              setCodeSent(false);
            }}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
              loginMode === 'password'
                ? 'bg-white text-neutral-900 shadow-sm'
                : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            <Lock className="h-4 w-4" />
            <span>По паролю</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setLoginMode('otp');
              setError('');
            }}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
              loginMode === 'otp'
                ? 'bg-white text-neutral-900 shadow-sm'
                : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            <Smartphone className="h-4 w-4" />
            <span>По коду</span>
          </button>
        </div>

        {/* Password Login Form */}
        {loginMode === 'password' && (
          <form onSubmit={handleSubmit} data-testid="form-login" className="space-y-5">
          {/* Username */}
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-neutral-700">
              Имя пользователя
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User className="h-5 w-5 text-neutral-400" />
              </div>
              <input
                type="text"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                required
                data-testid="input-username"
                autoComplete="username"
                placeholder="superadmin"
                className="block w-full pl-10 pr-4 py-2.5 rounded-xl border border-neutral-200 
                         text-neutral-900 placeholder-neutral-400
                         focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none
                         transition-all duration-200"
              />
            </div>
          </div>

          {/* Password */}
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-neutral-700">
              Пароль
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-neutral-400" />
              </div>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                data-testid="input-password"
                autoComplete="current-password"
                placeholder="••••••••"
                className="block w-full pl-10 pr-4 py-2.5 rounded-xl border border-neutral-200 
                         text-neutral-900 placeholder-neutral-400
                         focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none
                         transition-all duration-200"
              />
            </div>
          </div>

          {/* Tenant (optional) */}
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-neutral-700">
              Клиника <span className="text-neutral-400 font-normal">(опционально)</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Building2 className="h-5 w-5 text-neutral-400" />
              </div>
              <input
                type="text"
                value={form.tenant_slug}
                onChange={(e) => setForm({ ...form, tenant_slug: e.target.value })}
                data-testid="input-tenant"
                placeholder="например: dental clinic"
                className="block w-full pl-10 pr-4 py-2.5 rounded-xl border border-neutral-200 
                         text-neutral-900 placeholder-neutral-400
                         focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none
                         transition-all duration-200"
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div 
              className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 border border-red-100 text-red-600 text-sm animate-fade-in"
              data-testid="error-message"
            >
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            data-testid="btn-login"
            className="w-full flex items-center justify-center gap-2 px-4 py-3 
                     rounded-xl font-medium text-white
                     bg-gradient-to-r from-primary-600 to-primary-700
                     hover:from-primary-700 hover:to-primary-800
                     focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                     disabled:opacity-60 disabled:cursor-not-allowed
                     transition-all duration-200 shadow-lg shadow-primary-500/25
                     hover:shadow-xl hover:shadow-primary-500/30
                     active:scale-[0.98]"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Вход...</span>
              </>
            ) : (
              <>
                <LogIn className="h-5 w-5" />
                <span>Войти в систему</span>
              </>
            )}
          </button>
        </form>
        )}

        {/* OTP Login Form (TODO-054, 05.02.2026) */}
        {loginMode === 'otp' && (
          <div className="space-y-5">
            {!codeSent ? (
              // Step 1: Enter phone
              <>
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-neutral-700">
                    Номер телефона
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Smartphone className="h-5 w-5 text-neutral-400" />
                    </div>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                      placeholder="+79261234567"
                      className="block w-full pl-10 pr-4 py-2.5 rounded-xl border border-neutral-200 
                               text-neutral-900 placeholder-neutral-400
                               focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none
                               transition-all duration-200"
                    />
                  </div>
                </div>

                {error && (
                  <div 
                    className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 border border-red-100 text-red-600 text-sm animate-fade-in"
                  >
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => handleSendCode(false)}
                  disabled={isLoading || !phone}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 
                           rounded-xl font-medium text-white
                           bg-gradient-to-r from-primary-600 to-primary-700
                           hover:from-primary-700 hover:to-primary-800
                           focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                           disabled:opacity-60 disabled:cursor-not-allowed
                           transition-all duration-200 shadow-lg shadow-primary-500/25
                           hover:shadow-xl hover:shadow-primary-500/30
                           active:scale-[0.98]"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Отправка...</span>
                    </>
                  ) : (
                    <>
                      <Smartphone className="h-5 w-5" />
                      <span>Получить код</span>
                    </>
                  )}
                </button>
              </>
            ) : (
              // Step 2: Enter code
              <form onSubmit={handleVerifyCode} className="space-y-5">
                <div className="p-4 rounded-xl bg-green-50 border border-green-100 text-green-700 text-sm">
                  <p className="font-medium mb-1">Код отправлен!</p>
                  <p className="text-green-600">
                    {channel === 'waba' && 'Проверьте WhatsApp Business'}
                    {channel === 'whatsapp' && 'Проверьте WhatsApp'}
                    {channel === 'sms' && 'Проверьте SMS'}
                  </p>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-neutral-700">
                    Код подтверждения
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <KeyRound className="h-5 w-5 text-neutral-400" />
                    </div>
                    <input
                      type="text"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      required
                      maxLength={6}
                      placeholder="123456"
                      autoFocus
                      className="block w-full pl-10 pr-4 py-2.5 rounded-xl border border-neutral-200 
                               text-neutral-900 placeholder-neutral-400 text-center text-2xl font-mono tracking-widest
                               focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none
                               transition-all duration-200"
                    />
                  </div>
                </div>

                {error && (
                  <div 
                    className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 border border-red-100 text-red-600 text-sm animate-fade-in"
                  >
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading || code.length !== 6}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 
                           rounded-xl font-medium text-white
                           bg-gradient-to-r from-primary-600 to-primary-700
                           hover:from-primary-700 hover:to-primary-800
                           focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                           disabled:opacity-60 disabled:cursor-not-allowed
                           transition-all duration-200 shadow-lg shadow-primary-500/25
                           hover:shadow-xl hover:shadow-primary-500/30
                           active:scale-[0.98]"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Проверка...</span>
                    </>
                  ) : (
                    <>
                      <LogIn className="h-5 w-5" />
                      <span>Войти</span>
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => handleSendCode(true)}
                  disabled={isLoading}
                  className="w-full text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  Не пришел код? Отправить повторно
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setCodeSent(false);
                    setCode('');
                    setError('');
                  }}
                  className="w-full text-sm text-neutral-500 hover:text-neutral-700"
                >
                  Изменить номер телефона
                </button>
              </form>
            )}
          </div>
        )}
      </div>

      {/* Help text */}
      <p className="text-center text-white/50 text-sm mt-6">
        Проблемы со входом? Обратитесь к администратору
      </p>
    </AuthLayout>
  );
}
